/**
 * Runtime config: fetched at boot with no-store so one build
 * deploys to any environment. The price fields are now a *scalar fallback only*
 * — `usePricing()` reads the public pricing endpoint as the source of truth and
 * falls back to these (per-litre, pack fee) while it loads or if it's down;
 * per-preset prices come from the endpoint alone. `min/maxRechargePaisa` are
 * recharge limits, not pricing. Auth feature flags gate the email tab + social
 * buttons until their backend pieces land.
 */
import { createContext, useContext, type ReactNode } from "react";

export interface AppConfig {
  apiBaseUrl: string;
  pricePerLitrePaisa: number;
  cardPackFeePaisa: number;
  minRechargePaisa: number;
  maxRechargePaisa: number;
  supportPhone: string;
  features: {
    emailAuth: boolean;
    oauthProviders: string[];
  };
  /** Optional Sentry DSN — telemetry stays fully inert (never loaded) when absent. */
  sentryDsn?: string;
  /** Deploy environment tag for Sentry (e.g. "dev", "production"). */
  environment?: string;
  /** Release identifier for Sentry (e.g. the build sha). */
  release?: string;
}

export async function loadConfig(): Promise<AppConfig> {
  const response = await fetch("/config.json", { cache: "no-store" });
  if (!response.ok) throw new Error(`config.json failed: ${response.status}`);
  return (await response.json()) as AppConfig;
}

const ConfigContext = createContext<AppConfig | null>(null);

export function ConfigProvider({ config, children }: { config: AppConfig; children: ReactNode }) {
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

export function useConfig(): AppConfig {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error("useConfig must be used within ConfigProvider");
  return ctx;
}
